<template>
  <b-container>
    <b-row align-h="start" class="mb-4">
      <b-overlay :show="loading" no-wrap fixed />
      <b-col>
        <h1>Menu #{{ $route.params.id }}</h1>
      </b-col>
      <b-col class="text-right">
        <saved-notice v-if="saved" />
        <b-button
          v-if="!readOnly"
          :disabled="loading || categoriesLoading"
          class="mb-1 ml-2"
          variant="primary"
          @click="onSaveClick"
        >
          Save
        </b-button>
        <b-button
          class="mb-1 ml-2"
          variant="primary"
          @click="$router.push('/menus')"
        >
          Back
        </b-button>
      </b-col>
    </b-row>
    <error-alert :errors="errors" class="mb-3" />
    <b-tabs nav-wrapper-class="nav-wrapper-class" content-class="mt-3">
      <b-tab title="Menu Details" active class="menu-tab">
        <b-row class="base-form mb-3 mx-0">
          <b-col lg="6" sm="12">
            <b-form-group label="Name">
              <b-form-input v-model="menu.name" :disabled="readOnly" />
            </b-form-group>
            <custom-search-field
              v-model="menu.facility"
              :disabled="true"
              :placeholder="'Pick hub'"
              :path-to-search="'facilities'"
              group-label="Hub"
            />
          </b-col>
          <b-col lg="6" sm="12">
            <b-form-group class="mt-4">
              <b-form-checkbox
                v-model="menuActive"
                :disabled="readOnly"
                :value="true"
                :unchecked-value="false"
                class="mb-2 mt-2"
                ><span class="ml-2">Active</span></b-form-checkbox
              >
            </b-form-group>
            <b-form-group label="Tax Rate">
              <b-form-input
                v-model="menu.tax_rate"
                :disabled="readOnly"
                type="number"
                min="0"
                step=".000001"
              />
            </b-form-group>
          </b-col>
        </b-row>
        <b-row>
          <b-col sm="12" lg="6" class="mb-3">
            <table class="editable-table">
              <thead>
                <tr>
                  <th width="10%" />
                  <th class="text-left pl-3">Brands</th>
                  <th>Items</th>
                  <th width="10%" class="text-center" />
                </tr>
              </thead>
              <draggable :disabled="readOnly" :list="menu.brands" tag="tbody">
                <tr
                  v-for="(brand, brandIndex) in menu.brands"
                  :key="brand.id"
                  height="36px"
                >
                  <td class="text-center">
                    <b-icon v-if="!readOnly" icon="list"></b-icon>
                  </td>
                  <td class="pl-3">
                    {{ brand.name }}
                  </td>
                  <td>{{ getBrandItemCount(brand.id) }}</td>
                  <td class="py-0">
                    <b-button
                      v-if="!readOnly && getBrandItemCount(brand.id) === 0"
                      variant="white"
                      title="Remove Menu Category"
                      class="text-center text-primary"
                      @click="removeBrand(brandIndex)"
                    >
                      <b-icon icon="X" />
                    </b-button>
                  </td>
                </tr>
              </draggable>
              <tfoot v-if="!readOnly">
                <tr>
                  <td />
                  <td colspan="1" class="pl-1">
                    <custom-search-field
                      v-if="menu.facility_id"
                      v-model="newBrand"
                      class="mb-0 single-input"
                      placeholder="Type and click enter to add brand..."
                      :path-to-search="'brands'"
                      :query-params="{
                        facility: menu.facility_id,
                      }"
                    />
                  </td>
                </tr>
              </tfoot>
            </table>
          </b-col>
          <b-col sm="12" lg="6" class="mb-3">
            <b-overlay :show="categoriesLoading" no-wrap />
            <table class="editable-table">
              <thead>
                <tr>
                  <th width="10%" class="text-center px-sm-3 px-0">
                    <img
                      id="menus-table-header-categories"
                      :src="asset('/imgs/icons/info.svg')"
                      alt="hours info"
                      class="mb-1"
                    />
                  </th>
                  <th class="editable-header d-flex">Categories</th>
                  <b-tooltip
                    target="menus-table-header-categories"
                    triggers="hover"
                  >
                    Hover over existing categories to edit. Deleting a category
                    will remove all items in that category from the menu.
                  </b-tooltip>
                  <th>Items</th>
                  <th width="10%" />
                </tr>
              </thead>
              <draggable
                :disabled="readOnly"
                :list="menuCategories"
                tag="tbody"
              >
                <tr
                  v-for="(category, methodIndex) in menuCategories"
                  :key="category.id"
                >
                  <td class="text-center">
                    <b-icon v-if="!readOnly" icon="list"></b-icon>
                  </td>
                  <td class="editable-cell">
                    <b-form-input
                      v-model="category.name"
                      :disabled="readOnly"
                      class="single-input"
                    />
                  </td>
                  <td>
                    {{ category.menuCategoriesCount }}
                  </td>
                  <td>
                    <b-button
                      v-if="!readOnly"
                      variant="white"
                      title="Remove Menu Category"
                      :method-index="methodIndex"
                      class="text-center text-primary"
                      @click="removeMenuCategory"
                    >
                      <b-icon icon="X" />
                    </b-button>
                  </td>
                </tr>
              </draggable>
              <tfoot v-if="!readOnly">
                <tr>
                  <td />
                  <td colspan="1" class="pr-2">
                    <b-form-input
                      v-model="newCategory"
                      autocomplete="off"
                      class="single-input"
                      placeholder="Type and click enter to add categories..."
                      @keyup.enter.native="addNewCategory"
                    />
                  </td>
                </tr>
              </tfoot>
            </table>
          </b-col>
        </b-row>
      </b-tab>
      <b-tab title="Availability" class="container">
        <b-row class="base-form mb-3">
          <b-col>
            <p>
              This menu is available on the following networks. Please reach out
              to the Zuul team to make any adjustments.
            </p>
          </b-col>
        </b-row>
        <b-row class="overflow-auto mr-2 mr-sm-0">
          <b-col class="px-sm-3 px-3">
            <b-table
              hover
              no-local-sorting
              small
              :fields="fulfillmentTimeFields"
              :items="fulfillmentTimes"
              :busy="busy"
              @sort-changed="onFulfillmentTimeSortingChanged"
            >
              <template #cell(network)="row">
                {{ row.item.deliveryMethod.fulfillmentMethod.network.name }}
              </template>
              <template #cell(fulfillment_type)="row">
                {{
                  row.item.deliveryMethod.fulfillmentMethod.type
                    | fulfillmentTypeFormat
                    | capitalize
                }}
              </template>
              <template #cell(time)="row">
                {{ row.item.fulfillmentTime.time | formatTime }}
              </template>
              <template #cell(notice)="row">
                {{ row.item.fulfillmentTime.notice }}
              </template>
              <template #cell(monday)="row">
                <b-form-checkbox
                  v-model="row.item.fulfillmentTime.monday"
                  value="1"
                  unchecked-value="0"
                  disabled
                />
              </template>
              <template #cell(tuesday)="row">
                <b-form-checkbox
                  v-model="row.item.fulfillmentTime.tuesday"
                  value="1"
                  unchecked-value="0"
                  disabled
                />
              </template>
              <template #cell(wednesday)="row">
                <b-form-checkbox
                  v-model="row.item.fulfillmentTime.wednesday"
                  value="1"
                  unchecked-value="0"
                  disabled
                />
              </template>
              <template #cell(thursday)="row">
                <b-form-checkbox
                  v-model="row.item.fulfillmentTime.thursday"
                  value="1"
                  unchecked-value="0"
                  disabled
                />
              </template>
              <template #cell(friday)="row">
                <b-form-checkbox
                  v-model="row.item.fulfillmentTime.friday"
                  value="1"
                  unchecked-value="0"
                  disabled
                />
              </template>
              <template #cell(saturday)="row">
                <b-form-checkbox
                  v-model="row.item.fulfillmentTime.saturday"
                  value="1"
                  unchecked-value="0"
                  disabled
                />
              </template>
              <template #cell(sunday)="row">
                <b-form-checkbox
                  v-model="row.item.fulfillmentTime.sunday"
                  value="1"
                  unchecked-value="0"
                  disabled
                />
              </template>
            </b-table>
          </b-col>
        </b-row>
        <b-row align-h="between">
          <b-col>
            <p class="table-results">
              Displaying {{ paginationFrom }}-{{ paginationTo }} of
              {{ rows }} results
            </p>
          </b-col>
          <b-col>
            <b-pagination
              v-if="rows > perPage"
              v-model="currentPage"
              class="float-right"
              :total-rows="rows"
              :per-page="perPage"
              aria-controls="my-table"
              @input="onPaginate"
            />
          </b-col>
        </b-row>
      </b-tab>
    </b-tabs>
  </b-container>
</template>

<script>
import { mapGetters } from 'vuex';
import draggable from 'vuedraggable';
import ResourceAPI from '../../services/ResourceAPI';
import Menu from '../../models/Menu.js';
import ErrorAlert from '../../components/ErrorAlert';
import SavedNotice from '../../components/SavedNotice';

export default {
  components: {
    draggable,
    ErrorAlert,
    SavedNotice,
  },
  data() {
    return {
      loading: false,
      categoriesLoading: false,
      saved: false,
      errors: {},
      menu: new Menu({
        id: this.$route.params.id,
      }),
      menuActive: false,
      brands: [],
      newBrand: null,
      newCategory: null,
      menuCategories: [],
      fulfillmentTimes: [],
      busy: false,
      fulfillmentTimeFields: [
        {
          key: 'deliveryMethod.fulfillmentMethod.network.name',
          sortKey: 'networks.name',
          label: 'Network',
          sortable: true,
        },
        {
          key: 'deliveryMethod.fulfillmentMethod.name',
          label: 'Name',
          sortKey: 'fulfillment_methods.name',
          sortable: true,
        },
        {
          key: 'fulfillment_type',
          sortKey: 'fulfillment_methods.type',
          label: 'Fulfillment Type',
          sortable: true,
        },
        {
          key: 'time',
          sortKey: 'fulfillment_times.time',
          label: 'Time',
          sortable: true,
        },
        {
          key: 'fulfillmentTime.notice',
          sortKey: 'fulfillment_times.notice',
          label: 'Notice (min)',
          sortable: true,
          thStyle: { width: '105px' },
        },
        {
          key: 'monday',
          label: 'Mon',
        },
        {
          key: 'tuesday',
          label: 'Tue',
        },
        {
          key: 'wednesday',
          label: 'Wed',
        },
        {
          key: 'thursday',
          label: 'Thu',
        },
        {
          key: 'friday',
          label: 'Fri',
        },
        {
          key: 'saturday',
          label: 'Sat',
        },
        {
          key: 'sunday',
          label: 'Sun',
        },
      ],
      paginationFrom: null,
      paginationTo: null,
      currentPage: 1,
      perPage: 25,
      rows: 0,
      sort: '',
      dir: '',
    };
  },
  computed: {
    ...mapGetters('session', ['hubIds']),
    readOnly() {
      if (this.menu && this.hubIds.includes(this.menu.facility_id)) {
        return false;
      }

      return !!this.menu;
    },
  },
  watch: {
    newBrand(val) {
      if (val) {
        if (!this.menu.brands.filter((el) => el.id === val.id).length) {
          const brands = this.menu.get('brands');

          brands.push(val);
          this.menu.set('brands', brands);
        }
        this.newBrand = null;
      }
    },
  },
  mounted() {
    this.loadMenu();
    this.loadFulfillmentTimes();
  },
  methods: {
    loadMenu() {
      const me = this;

      me.loading = true;
      const menuRaw = new Menu({
        id: this.$route.params.id,
      });

      // This little bit tricky but after we asign new Menu on place of previous loading state overrides
      // current data with defaults from Models.js which leads to blinking data to 0 and back to normal
      // when async function returns result, instead of ours solutions i stoped on creating dummy variable
      // which will contain new data and replace current me.menu after loading of response. Blinking doesn't
      // happends because new data should be the same as we have. Solution with matching data won't work
      // because we modificate data before POST on lines 523-529

      menuRaw
        .fetch()
        .then(() => {
          me.menuActive = me.menu.status === 'active';
          me.loadMenuCategories();
          me.loading = false;
          me.menu = menuRaw;
        })
        .catch((error) => alert(error));
    },

    loadMenuCategories() {
      const me = this;

      me.categoriesLoading = true;

      ResourceAPI.getRecords('menu-categories', {
        menu: me.$route.params.id,
        limit: 100,
        sort: 'priority',
        dir: 'asc',
        appends: 'menuCategoriesCount',
      })
        .then((data) => {
          me.menuCategories = data.data;
          me.categoriesLoading = false;
        })
        .catch((error) => alert(error));
    },

    addNewCategory() {
      this.menuCategories.push({
        name: this.newCategory,
        menuCategoriesCount: 0,
      });
      this.newCategory = null;
    },

    removeMenuCategory(event) {
      this.menuCategories.splice(
        Number(event.currentTarget.getAttribute('method-index')),
        1
      );
    },

    // Return the number of brand items tied to a menu
    getBrandItemCount(brandId) {
      if (brandId in this.menu.brandMenuItemCount) {
        return this.menu.brandMenuItemCount[brandId];
      }

      return 0;
    },

    removeBrand(index) {
      this.menu.brands.splice(index, 1);
    },

    onSaveClick() {
      const me = this;
      const menuCategories = [];
      let i;

      if (me.loading || me.categoriesLoading) {
        return;
      }

      me.loading = true;

      // Build menu categories array
      for (i = 0; i < me.menuCategories.length; i += 1) {
        menuCategories.push({
          name: me.menuCategories[i].name,
          id: me.menuCategories[i].id,
        });
      }

      me.menu.set('status', me.menuActive ? 'active' : 'inactive');
      me.menu.set(
        'brandIds',
        me.menu.brands.map((a) => a.id)
      );
      me.menu.set('menuCategories', menuCategories);
      me.menu.set('facility_id', me.menu.facility ? me.menu.facility.id : null);

      me.menu
        .save()
        .then(() => {
          me.saved = true;
          setTimeout(() => {
            me.saved = false;
          }, 5000);
          me.errors = {};
          me.loadMenu();
        })
        .catch((error) => {
          me.saved = false;
          me.loading = false;
          me.errors = error.response.data.errors;
        });
    },

    onPaginate(page) {
      this.currentPage = page;
      this.loadFulfillmentTimes();
    },

    loadFulfillmentTimes() {
      const me = this;

      me.busy = true;

      ResourceAPI.getRecords('fulfillment-times', {
        limit: me.perPage,
        page: me.currentPage,
        sort: me.sort,
        dir: me.dir,
        menu: me.$route.params.id,
        with: 'fulfillmentTime,deliveryMethod.fulfillmentMethod.network',
        status: 'active',
        networkStatus: 'active',
      })
        .then((data) => {
          me.rows = data.meta.total;
          me.paginationFrom = data.meta.from;
          me.paginationTo = data.meta.to;
          me.fulfillmentTimes = data.data;
          me.busy = false;
        })
        .catch((error) => alert(error));
    },

    onFulfillmentTimeSortingChanged(ctx) {
      this.sort = ctx.sortBy;
      this.dir = ctx.sortDesc ? 'DESC' : 'ASC';
      this.loadFulfillmentTimes();
    },
  },
};
</script>
